import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useT } from "../lib/i18n";
import { api } from "../lib/api";
import LanguageToggle from "../components/LanguageToggle";

export default function AdminLogin() {
    const { t } = useT();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/auth/admin/login", { email, password });
            localStorage.setItem("sc_admin_token", data.access_token);
            localStorage.setItem("sc_admin_email", data.email);
            navigate("/admin", { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Login failed.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-[100dvh] text-ink-primary relative flex items-center justify-center px-6" data-testid="admin-login-page">
            <div className="absolute top-6 right-6">
                <LanguageToggle />
            </div>
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0 bg-[radial-gradient(50%_40%_at_50%_30%,rgba(212,175,55,0.10),transparent_70%)]" />
            </div>

            <motion.form
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                onSubmit={onSubmit}
                className="relative w-full max-w-md p-10 rounded-sm glass-strong gold-border-anim"
                data-testid="admin-login-form"
            >
                <div className="flex items-center gap-3 mb-2">
                    <Shield size={16} className="text-gold" />
                    <span className="label-eyebrow !text-gold">{t("admin.eyebrow")}</span>
                </div>
                <h1 className="font-serif font-medium text-3xl">{t("admin.login_title")}</h1>
                <p className="text-ink-secondary text-sm mt-2">{t("admin.login_sub")}</p>

                <div className="mt-8 space-y-6">
                    <div>
                        <label className="label-eyebrow block mb-2">{t("admin.email")}</label>
                        <input
                            type="email"
                            data-testid="admin-email-input"
                            className="input-line"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="email"
                            required
                        />
                    </div>
                    <div>
                        <label className="label-eyebrow block mb-2">{t("admin.password")}</label>
                        <input
                            type="password"
                            data-testid="admin-password-input"
                            className="input-line"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="current-password"
                            required
                        />
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={busy}
                    data-testid="admin-login-submit"
                    className="mt-10 w-full inline-flex items-center justify-center gap-3 px-10 py-3.5 bg-gold text-black rounded-sm font-medium tracking-wide hover:bg-gold-bright transition-all disabled:opacity-50"
                >
                    {busy ? <Loader2 size={18} className="animate-spin" /> : t("admin.login_cta")}
                </button>
            </motion.form>
        </div>
    );
}
